import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { api } from "@courses-models/license/src/ts/config";
import { IEkeyLicenseModel } from "@courses-models/license/src/ts/models/IEkeyLicenseModel";
import { ILicenseModel } from "@courses-models/license/src/ts/models/ILicenseModel";

/**
 * Describes data returned in the "MyRoles" prop on the endpoint response
 * defined below.
 */
type Ekey_License_Role = {
  Id: number;
  LongName: string;
  RoleType: number;
};

/**
 * Describes data returned in the "Product" prop on the endpoint response
 * defined below.
 */
type Ekey_License_Product = {
  Description: string;
  Id: string;
  ImageUrl: string;
  Isbn: string;
  ProductUrl: string;
  Title: string;
};

/**
 * Describes data returned by License/GetProductLicensesByUser from Gyldendal
 * Access Service.
 */
type License_GetProductLicensesByUser_Response = {
  EkeyId: string;
  CustomerId: string;
  InstitutionNumber: string;
  CustomerName: string;
  MyRoles: Ekey_License_Role[];
  Product: Ekey_License_Product;
}[];

/**
 * Describes data returned by v1/Unic/Products/User/{unicUserId} from Gyldendal
 * Access Service.
 */
type v1_Unic_Products_User_Response = {
  Id: string;
  Name: string;
  Context: String;
}[];

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<LicenseCollection>({ ttl: 600 });

/**
 * Iterable collection of users.
 */
export class LicenseCollection<
  T extends ILicenseModel = ILicenseModel
> extends Collection<T> {
  /**
   * Contains an internal set containing all users available within this
   * collection.
   */
  protected readonly collection: T[];

  /**
   * Cache collection of user's when this model is instantiated.
   */
  constructor(collection: T[]) {
    super();

    // Convert list of courses to an internal set
    this.collection = collection;
  }

  /**
   * Fetches a collection of licenses associated with a given e-key user.
   */
  @nonConcurrent()
  public static async GET_BY_EKEY_USER_ID(
    id: string
  ): Promise<LicenseCollection<IEkeyLicenseModel>> {
    const cacheId = `Ekey/${id}`;

    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(cacheId)) {
      return <LicenseCollection<IEkeyLicenseModel>>cache.get(cacheId);
    }

    // Fetch the list of licenses that were made available to this user, so
    // that we can identify his role type
    const endpoint = `${api.gyldendalAccessService(
      undefined
    )}/api/License/GetProductLicensesByUser?ekeyUserId=${encodeURIComponent(
      id
    )}`;
    const response = await axios.get<License_GetProductLicensesByUser_Response>(
      gGateway(endpoint)
    );

    // Format the items into well-structured data
    const collection = new LicenseCollection(
      response.data.map((item) => ({
        id: item.EkeyId,
        institutionId: item.InstitutionNumber,
        productId: item.Product.Id,
        productIsbn: item.Product.Isbn,
        productTitle: item.Product.Title,
        roleTypes: item.MyRoles.map((role) => role.RoleType),
      }))
    );

    // Cache the collection of licenses for future usage
    cache.set(cacheId, collection);

    // Return the cache publicly
    return collection;
  }

  /**
   * Fetches a collection of licenses associated with a given Unic user.
   */
  @nonConcurrent()
  public static async GET_BY_UNIC_USER_ID(
    id: string
  ): Promise<LicenseCollection> {
    const cacheId = `Unic/${id}`;

    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(cacheId)) {
      return <LicenseCollection>cache.get(cacheId);
    }

    // Fetch the list of licenses that were made available to this user, so
    // that we can identify his role type
    const endpoint = `${api.gyldendalAccessService(
      "v1"
    )}/api/v1/Unic/Products/User/${encodeURIComponent(id)}`;
    const response = await axios.get<v1_Unic_Products_User_Response>(gGateway(endpoint));

    // Format the items into well-structured data
    const collection = new LicenseCollection(
      response.data.map((item) => ({
        id: item.Id,
        productIsbn: item.Id,
        productTitle: item.Name,
      }))
    );

    // Cache the collection of licenses for future usage
    cache.set(cacheId, collection);

    // Return the cache publicly
    return collection;
  }

  /**
   * Helper that'll determine if this collection of licenses contains a
   * product by the given id.
   */
  public contains(isbn: string): boolean {
    const isbn10 = isbn.substring(isbn.length - 10);
    const isbn13 = `978${isbn10}`;

    for (const license of this.collection) {
      if (license.productIsbn === isbn10 || license.productIsbn === isbn13) {
        return true;
      }
    }

    // If we get here, no license matches the isbn - return false
    return false;
  }
}
